import { createFileRoute, Link } from "@tanstack/react-router";
import {
  BookOpen,
  ShoppingCart,
  Calendar,
  Car,
  BookMarked,
  Lock,
  ChevronRight,
  Feather,
  Settings2,
} from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { DCard, HeroCard } from "@/components/dryvn";
import { useMe } from "@/lib/queries";

export const Route = createFileRoute("/more/")({
  head: () => ({
    meta: [
      { title: "More — Dryvn Life" },
      {
        name: "description",
        content: "Journal, shopping, dates, vehicles, reading, legacy, and the vault.",
      },
    ],
  }),
  component: More,
});

const items = [
  { to: "/more/journal", label: "Journal", desc: "Mood & gratitude", Icon: BookOpen },
  { to: "/more/shopping", label: "Shopping list", desc: "Household needs", Icon: ShoppingCart },
  { to: "/more/dates", label: "Important dates", desc: "Birthdays, reminders", Icon: Calendar },
  { to: "/more/vehicles", label: "Vehicles", desc: "Service history", Icon: Car },
  { to: "/more/reading", label: "Reading log", desc: "Books & articles", Icon: BookMarked },
  { to: "/more/legacy", label: "Legacy", desc: "Letters, wishes, memory", Icon: Feather },
  { to: "/more/vault", label: "Vault", desc: "Secure references", Icon: Lock },
  { to: "/more/settings", label: "Settings", desc: "Integrations, API, account", Icon: Settings2 },
] as const;

// 'viewer' is the teen/child family role — the private and config surfaces
// aren't theirs. The server 403s the same paths; hiding here is courtesy.
const VIEWER_HIDDEN = new Set(["/more/journal", "/more/legacy", "/more/vault", "/more/settings"]);

function More() {
  const { data: me } = useMe();
  const visible = me?.role === "viewer" ? items.filter((i) => !VIEWER_HIDDEN.has(i.to)) : items;
  return (
    <AppShell>
      <HeroCard
        eyebrow="Everything else"
        title={
          <>
            Kept{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              simple
            </em>
            .
          </>
        }
      />

      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map(({ to, label, desc, Icon }) => (
          <Link key={to} to={to}>
            <DCard className="flex items-center gap-3 transition-all hover:-translate-y-0.5">
              <span
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full"
                style={{
                  backgroundColor: "color-mix(in oklab, var(--dryvn-life) 30%, transparent)",
                }}
              >
                <Icon className="h-5 w-5" style={{ color: "var(--dryvn-primary-deep)" }} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
                  {label}
                </p>
                <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                  {desc}
                </p>
              </div>
              <ChevronRight className="h-4 w-4" style={{ color: "var(--dryvn-grey)" }} />
            </DCard>
          </Link>
        ))}
      </div>
    </AppShell>
  );
}
